import { CommunityDimensions, CommunityTalent } from "../models/CommunityDashboard";

export const ALL = -1;
export const NOT_LOCATED = -2;
export const NOT_SPECIFIED = -3;

export const ALL_VALUE = "__all__";
export const NOT_LOCATED_VALUE = "__notLocated__";

export interface CommunityFilters {
	regionIndex: number;
	countryIndex: number;
	categoryIndex: number;
	expertiseIndex: number;
	sectorIndex: number;
}

export const emptyFilters: CommunityFilters = {
	regionIndex: ALL,
	countryIndex: ALL,
	categoryIndex: ALL,
	expertiseIndex: ALL,
	sectorIndex: ALL
};

export interface CommunityFilterSelection {
	region: string;
	country: string;
	category: string;
	expertise: string;
	sector: string;
}

export const emptySelection: CommunityFilterSelection = {
	region: ALL_VALUE,
	country: ALL_VALUE,
	category: ALL_VALUE,
	expertise: ALL_VALUE,
	sector: ALL_VALUE
};

const indexOrAll = (index: number): number => (index >= 0 ? index : ALL);

export const toFilterIndexes = (
	selection: CommunityFilterSelection,
	dimensions: CommunityDimensions
): CommunityFilters => ({
	regionIndex:
		selection.region === ALL_VALUE
			? ALL
			: selection.region === NOT_LOCATED_VALUE
				? NOT_LOCATED
				: indexOrAll(dimensions.regions.indexOf(selection.region)),
	countryIndex:
		selection.country === ALL_VALUE
			? ALL
			: selection.country === NOT_LOCATED_VALUE
				? NOT_LOCATED
				: indexOrAll(
						dimensions.countries.findIndex(country => country.code === selection.country)
					),
	categoryIndex:
		selection.category === ALL_VALUE
			? ALL
			: indexOrAll(dimensions.expertiseCategories.indexOf(selection.category)),
	expertiseIndex:
		selection.expertise === ALL_VALUE
			? ALL
			: indexOrAll(
					dimensions.expertises.findIndex(
						expertise => String(expertise.id) === selection.expertise
					)
				),
	sectorIndex:
		selection.sector === ALL_VALUE
			? ALL
			: indexOrAll(dimensions.sectors.indexOf(selection.sector))
});

export const sanitizeSelection = (
	selection: CommunityFilterSelection,
	dimensions: CommunityDimensions
): CommunityFilterSelection => {
	const keep = (value: string, exists: boolean) => (value === ALL_VALUE || exists ? value : ALL_VALUE);
	const sanitized: CommunityFilterSelection = {
		region: keep(
			selection.region,
			selection.region === NOT_LOCATED_VALUE || dimensions.regions.includes(selection.region)
		),
		country: keep(
			selection.country,
			selection.country === NOT_LOCATED_VALUE ||
				dimensions.countries.some(country => country.code === selection.country)
		),
		category: keep(selection.category, dimensions.expertiseCategories.includes(selection.category)),
		expertise: keep(
			selection.expertise,
			dimensions.expertises.some(expertise => String(expertise.id) === selection.expertise)
		),
		sector: keep(selection.sector, dimensions.sectors.includes(selection.sector))
	};
	return sanitized.region === selection.region &&
		sanitized.country === selection.country &&
		sanitized.category === selection.category &&
		sanitized.expertise === selection.expertise &&
		sanitized.sector === selection.sector
		? selection
		: sanitized;
};

const matchesLocation = (talentIndex: number | null, filterIndex: number): boolean => {
	if (filterIndex === ALL) return true;
	if (filterIndex === NOT_LOCATED) return talentIndex === null;
	return talentIndex === filterIndex;
};

export const filterTalents = (
	talents: CommunityTalent[],
	filters: CommunityFilters
): CommunityTalent[] =>
	talents.filter(
		talent =>
			matchesLocation(talent.regionIndex, filters.regionIndex) &&
			matchesLocation(talent.countryIndex, filters.countryIndex) &&
			(filters.categoryIndex === ALL || talent.categoryIndex === filters.categoryIndex) &&
			(filters.expertiseIndex === ALL || talent.expertiseIndex === filters.expertiseIndex) &&
			(filters.sectorIndex === ALL || talent.sectorIndexes.includes(filters.sectorIndex))
	);

export const countSingle = (
	talents: CommunityTalent[],
	select: (talent: CommunityTalent) => number | null,
	nullKey?: number
): Record<number, number> => {
	const counts: Record<number, number> = {};
	talents.forEach(talent => {
		const value = select(talent);
		const key = value === null ? nullKey : value;
		if (key === undefined) return;
		counts[key] = (counts[key] || 0) + 1;
	});
	return counts;
};

export const countMulti = (
	talents: CommunityTalent[],
	select: (talent: CommunityTalent) => number[]
): Record<number, number> => {
	const counts: Record<number, number> = {};
	talents.forEach(talent => {
		select(talent).forEach(key => {
			counts[key] = (counts[key] || 0) + 1;
		});
	});
	return counts;
};

export interface AggregatedRow {
	key: number;
	count: number;
	pct: number;
}

export const toSortedRows = (counts: Record<number, number>, base: number): AggregatedRow[] =>
	Object.keys(counts)
		.map(key => {
			const index = Number(key);
			const count = counts[index];
			return { key: index, count, pct: base > 0 ? (count * 100) / base : 0 };
		})
		.sort((a, b) => b.count - a.count || a.key - b.key);

export const countLocated = (talents: CommunityTalent[]): number =>
	talents.filter(talent => talent.regionIndex !== null).length;
